import { AppBar, Box, Button, Container, Divider, Link, Stack, Toolbar, Typography, useScrollTrigger } from '@mui/material';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import { Logo } from './Logo';

/** Full legal set, rendered in the footer of every public page. */
export const LEGAL_LINKS = [
  { label: 'Privacy Policy', to: '/legal/privacy' },
  { label: 'Terms of Service', to: '/legal/terms' },
  { label: 'Cookie Policy', to: '/legal/cookies' },
  { label: 'Acceptable Use', to: '/legal/acceptable-use' },
  { label: 'Security', to: '/legal/security' },
  { label: 'Contact', to: '/legal/contact' },
] as const;

function PublicHeader() {
  // Transparent over the hero, solid once scrolled — keeps the hero clean
  // without losing the header.
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 24 });

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="transparent"
      sx={{
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        backgroundColor: scrolled ? 'background.paper' : 'transparent',
        borderBottom: 1,
        borderColor: scrolled ? 'divider' : 'transparent',
        transition: 'background-color 160ms, border-color 160ms',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ gap: 2 }}>
          <Link component={RouterLink} to="/" sx={{ display: 'flex' }} aria-label="HXIQ home">
            <Logo />
          </Link>
          <Box sx={{ flex: 1 }} />
          <Button component={RouterLink} to="/demo" color="inherit">
            Demo
          </Button>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

function PublicFooter() {
  return (
    <Box component="footer" sx={{ mt: 'auto', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 2 }}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Stack spacing={0.75}>
            <Logo size={26} />
            <Typography variant="body2" color="text.secondary">
              Heat Exchanger IQ
            </Typography>
          </Stack>

          <Stack
            direction="row"
            useFlexGap
            sx={{
              flexWrap: 'wrap',
              columnGap: 3,
              rowGap: 1.25,
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
            component="nav"
            aria-label="Legal"
          >
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.to}
                component={RouterLink}
                to={link.to}
                variant="body2"
                color="text.secondary"
              >
                {link.label}
              </Link>
            ))}
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} HXIQ. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}

export function PublicLayout() {
  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
      <PublicFooter />
    </Box>
  );
}
